import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deactivateCustomerAction, getCustomerAction, getFilteredCustomerAction } from "../../../store/actions/customer/CustomerActions";
import Swal from "sweetalert2";
import ShimmerLoader from "../../pages/ShimmerLoader/ShimmerLoader";
import Styles from "./Customer.module.css";
import NoDataFound from "../common/NoDataFound";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import copy from "copy-to-clipboard";
import { toast } from "react-toastify";

const searchSchema = Yup.object().shape({
  searchKey: Yup.string().required("Select a search key"),
  searchValue: Yup.string()
    .required("Enter a search value")
    .max(30, "Search value cannot exceed 30 characters")
    .test("not-blank", "Search value cannot be blank", (value) => value && value.trim() !== ""),
});

const CustomersList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const countPerPage = Number(process.env.REACT_APP_ITEMS_PER_PAGE);

  useEffect(() => {
    const payload = {
      page_number: currentPage,
      order_by: "updated_at",
      order_direction: "DESC",
    };
    dispatch(getCustomerAction(payload, navigate));
  }, [dispatch, currentPage]);

  const { customers, error, loading, message, totalCount } = useSelector((state) => state.customer);

  const handleStatusChange = (customerId, isActive) => {
    let status = isActive ? "deactivate" : "activate";
    Swal.fire({
      title: "Are you sure?",
      text: `You want to ${status} this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        let payload = {
          is_active: !isActive,
        };
        let customerPayload = {
          page_number: currentPage,
          order_by: "updated_at",
          order_direction: "DESC",
        };
        dispatch(deactivateCustomerAction(payload, customerId, navigate, customerPayload));
      }
    });
  };

  const onPageChange = (increment) => {
    setCurrentPage((prevPage) => Math.max(1, prevPage + increment));
  };

  const totalPages = Math.ceil(totalCount / countPerPage);

  const handleRowClick = (customer_id) => {
    navigate(`/view-customer-details/${customer_id}`);
  };

  const handleCopyToClipboard = (empId, event) => {
    event.stopPropagation();
    copy(empId);
    toast.success(`Copied  to clipboard!`, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 2000,
    });
  };

  return (
    <>
      <div className='row page-titles'>
        <ol className='breadcrumb'>
          <li className='breadcrumb-item active'>
            <Link to={"#"}>Customers</Link>
          </li>
          {/* <li className='breadcrumb-item'>
            <Link to={"#"}>All Customers</Link>
          </li> */}
        </ol>
      </div>

      <div className='row'>
        <div className='col-lg-12'>
          <div className='card'>
            <div className='card-header'>
              <h4 className='card-title'>All Customers</h4>
              <div className='d-flex'>
                <Formik
                  initialValues={{ searchKey: "phone", searchValue: "" }}
                  validationSchema={searchSchema}
                  onSubmit={(values) => {
                    const trimmedSearchValue = values && values.searchValue.trim();
                    setCurrentPage(1);
                    const payload = {
                      module_name: "customer",
                      search_key: values.searchKey,
                      search_value: trimmedSearchValue,
                      page_number: 1,
                    };
                    dispatch(getFilteredCustomerAction(payload, navigate));
                  }}
                  onReset={() => {
                    setCurrentPage(1);
                    const payload = {
                      page_number: 1,
                      order_by: "updated_at",
                      order_direction: "DESC",
                    };
                    dispatch(getCustomerAction(payload, navigate));
                  }}
                >
                  {({ handleReset, touched, errors, values }) => (
                    <Form className='d-flex'>
                      <Field as='select' name='searchKey' className={`form-select me-2 ${Styles.fixedWidth}`}>
                        {/* <option value='' disabled>
                          Select One
                        </option> */}
                        <option value='phone'>Phone</option>
                        <option value='display_name'>Customer Name</option>
                        <option value='email'>Email</option>
                      </Field>

                      <div className='position-relative flex-grow-1 me-2'>
                        <Field type='text' name='searchValue' className={`form-control ps-4 pe-5`} maxLength={30} placeholder='Search' aria-label='Search' />
                      </div>
                      <button className='btn btn-primary mx-1' type='submit' disabled={!values.searchKey || !values.searchValue}>
                        Search
                      </button>
                      <button type='reset' className='btn btn-outline-secondary mx-1' onClick={handleReset} disabled={!values.searchKey && !values.searchValue}>
                        Clear
                      </button>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>

            <div className='card-body'>
              <div className='table-responsive ticket-table'>
                <div id='ticket_wrapper' className='dataTables_wrapper no-footer'>
                  <div className='table-responsive'>
                    <table id='example' className='display dataTablesCard table-responsive-xl dataTable no-footer w-100'>
                      <thead>
                        <tr>
                          <th className={`${Styles.smallWidth}`}>Phone</th>
                          <th>Customer Name</th>
                          <th className='text-center'>Email</th>
                          <th className={`${Styles.smallWidth}`}>Date of Birth</th>
                          <th className={`${Styles.smallWidth}`}>City</th>
                          <th className={`text-center ${Styles.largeWidth}`}>KYC Status</th>
                          <th className={`${Styles.smallWidth}`}>Active/Inactive</th>
                          <th className={`${Styles.smallWidth}`}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={8} className='text-center'>
                              <ShimmerLoader width='95%' height='20%' count={5} />
                            </td>
                          </tr>
                        ) : customers.length > 0 ? (
                          customers.map((item, index) => (
                            <tr key={index}>
                              <td className='text-center' style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                                <Link className={`${Styles.link}`} to={`/view-customer-details/${item.customer_id}`}>
                                  {item.phone ? item.phone : "--"}
                                </Link>
                                <svg style={{ marginLeft: "8px", cursor: "pointer" }} onClick={(e) => handleCopyToClipboard(item.phone, e)} xmlns='http://www.w3.org/2000/svg' height='24px' viewBox='0 -960 960 960' width='24px' fill='#c3992e'>
                                  <path d='M362.31-260Q332-260 311-281q-21-21-21-51.31v-455.38Q290-818 311-839q21-21 51.31-21h335.38Q728-860 749-839q21 21 21 51.31v455.38Q770-302 749-281q-21 21-51.31 21H362.31Zm0-60h335.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-455.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H362.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v455.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85Zm-140 200Q192-120 171-141q-21-21-21-51.31v-515.38h60v515.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h395.38v60H222.31ZM350-320v-480 480Z' />
                                </svg>
                              </td>
                              <td className='text-center'>{item.name ? item.name : "--"}</td>
                              <td className='text-center'>{item.email ? item.email : "--"}</td>
                              <td className='text-center'>{item.dob ? item.dob : "--"}</td>
                              <td>{item.city ? item.city : "--"}</td>
                              <td className='text-center'>{item.kyc_verified ? <span className='text-success fw-bold'>Verified</span> : <span className='text-danger fw-bold'>Not Verified</span>}</td>
                              <td className='d-flex justify-content-center'>
                                <div className='form-check form-switch'>
                                  <input className='form-check-input' style={{ cursor: "pointer" }} type='checkbox' role='switch' id={`flexSwitchCheckChecked-${item.customer_id}`} checked={item.is_active} onChange={() => handleStatusChange(item.customer_id, item.is_active)} />
                                  <label className='form-check-label' htmlFor={`flexSwitchCheckChecked-${item.customer_id}`}></label>
                                </div>
                              </td>
                              <td>
                                <div className='d-flex justify-content-start'>
                                  <Link to={`/edit-customer/${item.customer_id}`} className='btn btn-primary shadow btn-xs sharp me-1 mx-3'>
                                    <i className='fas fa-pencil-alt'></i>
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className={`${Styles.no_hover}`}>
                            <td className={`fs-3 text-center`} colSpan={8}>
                              <NoDataFound width={500} />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {!loading && customers.length > 0 && (
                    <div className='d-flex justify-content-end align-items-center px-4'>
                      <div className='d-sm-flex justify-content-end align-items-center ms-3 mt-3 mb-3'>
                        <div className='dataTables_paginate paging_simple_numbers mb-0 d-flex align-items-center' id='example2_paginate'>
                          <i className={`fa fa-chevron-circle-left fa-2x ${currentPage === 1 ? "text-muted" : "text-primary"}`} onClick={() => currentPage > 1 && onPageChange(-1)} style={{ cursor: currentPage === 1 ? "not-allowed" : "pointer" }}></i>
                          <span className='mx-2'>
                            Page {currentPage} of {totalPages}
                          </span>
                          <i className={`fa fa-chevron-circle-right fa-2x ${currentPage === totalPages || totalPages === 0 ? "text-muted" : "text-primary"}`} onClick={() => currentPage < totalPages && onPageChange(1)} style={{ cursor: currentPage === totalPages || totalPages === 0 ? "not-allowed" : "pointer" }}></i>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomersList;
